// Optional local recording: a SECOND, independent encode at fixed high
// quality (unaffected by adaptive-bitrate dips) written straight to a
// user-chosen file while streaming.
//
// Desktop-only by design: requires showSaveFilePicker (Chrome/Edge). The file
// is written as *fragmented* MP4 so it stays playable up to the last fragment
// even if the tab crashes mid-recording. Audio is AAC when the platform can
// encode it (Chrome on macOS/Windows), otherwise Opus.
//
// Timeline: the engine's pipelines re-base timestamps to 0 and restart on
// publish restarts (reconnects, stop/go-live) — the recorder stitches those
// generations into one continuous timeline via per-track offsets.

import {
  Output,
  Mp4OutputFormat,
  StreamTarget,
  EncodedVideoPacketSource,
  EncodedAudioPacketSource,
  EncodedPacket,
} from "mediabunny";

export const RECORDING_VIDEO_BITRATE = 8_000_000;
const RECORDING_AUDIO_BITRATE = 192_000;
const KEYFRAME_INTERVAL_US = 2_000_000;

export function isLocalRecordingSupported() {
  return (
    typeof window !== "undefined" &&
    typeof window.showSaveFilePicker === "function" &&
    typeof VideoEncoder !== "undefined" &&
    typeof AudioEncoder !== "undefined"
  );
}

// Must be called directly from a user gesture. Returns null if cancelled.
// `description` names the file type in the save dialog; the UI passes it in
// the user's language.
export async function chooseRecordingFile(
  suggestedName,
  description = "MP4 video"
) {
  try {
    return await window.showSaveFilePicker({
      suggestedName,
      types: [{ description, accept: { "video/mp4": [".mp4"] } }],
    });
  } catch (e) {
    if (e?.name === "AbortError") return null;
    throw e;
  }
}

async function pickRecordingAudioCodec(sampleRate, numberOfChannels) {
  try {
    const { supported } = await AudioEncoder.isConfigSupported({
      codec: "mp4a.40.2",
      sampleRate,
      numberOfChannels,
      bitrate: RECORDING_AUDIO_BITRATE,
    });
    if (supported) return { mediabunny: "aac", webcodecs: "mp4a.40.2" };
  } catch {
    // fall through to opus
  }
  return { mediabunny: "opus", webcodecs: "opus" };
}

export async function createLocalRecorder({
  fileHandle,
  videoCodec,
  width,
  height,
  fps,
  sampleRate,
  onError,
}) {
  const writable = await fileHandle.createWritable();
  const fileStream = new WritableStream({
    write(chunk) {
      return writable.write({
        type: "write",
        position: chunk.position,
        data: chunk.data,
      });
    },
    close() {
      return writable.close();
    },
  });

  const output = new Output({
    target: new StreamTarget(fileStream),
    format: new Mp4OutputFormat({
      fastStart: "fragmented",
      minimumFragmentDuration: 2,
    }),
  });
  const videoSource = new EncodedVideoPacketSource("avc");
  output.addVideoTrack(videoSource, { frameRate: fps });
  const audioCodec = await pickRecordingAudioCodec(sampleRate, 1);
  const audioSource = new EncodedAudioPacketSource(audioCodec.mediabunny);
  output.addAudioTrack(audioSource);

  let chain = output.start();
  const enqueue = (fn) => {
    chain = chain.then(fn).catch((e) => onError?.(e));
  };

  let videoMetaSent = false;
  let audioMetaSent = false;
  let stopped = false;

  const videoEncoder = new VideoEncoder({
    output: (chunk, meta) => {
      enqueue(() => {
        const first = !videoMetaSent;
        videoMetaSent = true;
        return videoSource.add(
          EncodedPacket.fromEncodedChunk(chunk),
          first ? { decoderConfig: meta.decoderConfig } : undefined
        );
      });
    },
    error: (e) => onError?.(e),
  });
  videoEncoder.configure({
    codec: videoCodec,
    avc: { format: "avc" },
    width,
    height,
    bitrate: RECORDING_VIDEO_BITRATE,
    framerate: fps,
  });

  const audioEncoder = new AudioEncoder({
    output: (chunk, meta) => {
      enqueue(() => {
        const first = !audioMetaSent;
        audioMetaSent = true;
        return audioSource.add(
          EncodedPacket.fromEncodedChunk(chunk),
          first ? { decoderConfig: meta.decoderConfig } : undefined
        );
      });
    },
    error: (e) => onError?.(e),
  });
  audioEncoder.configure({
    codec: audioCodec.webcodecs,
    sampleRate,
    numberOfChannels: 1,
    bitrate: RECORDING_AUDIO_BITRATE,
  });

  // Per-track stitching across pipeline restarts (each restart re-bases to 0).
  const makeTimeline = (gapUs) => ({
    offset: 0,
    last: -1,
    map(ts) {
      let t = ts + this.offset;
      if (t <= this.last) {
        this.offset = this.last + gapUs - ts;
        t = ts + this.offset;
      }
      this.last = t;
      return t;
    },
  });
  const videoTimeline = makeTimeline(33_000);
  const audioTimeline = makeTimeline(20_000);
  let lastKeyTs = -Infinity;

  return {
    encodeVideoFrame(frame) {
      if (stopped || videoEncoder.state !== "configured") return;
      if (videoEncoder.encodeQueueSize > 8) return; // shed load rather than stall capture
      const ts = videoTimeline.map(frame.timestamp);
      const recFrame = new VideoFrame(frame, { timestamp: ts });
      const keyFrame = ts - lastKeyTs >= KEYFRAME_INTERVAL_US;
      if (keyFrame) lastKeyTs = ts;
      try {
        videoEncoder.encode(recFrame, { keyFrame });
      } finally {
        recFrame.close();
      }
    },
    encodeAudioData(audioData) {
      if (stopped || audioEncoder.state !== "configured") return;
      // AudioData can only be encoded once — make a copy on the recorder's
      // stitched timeline.
      const copy = new AudioData({
        format: audioData.format,
        sampleRate: audioData.sampleRate,
        numberOfFrames: audioData.numberOfFrames,
        numberOfChannels: audioData.numberOfChannels,
        timestamp: audioTimeline.map(audioData.timestamp),
        data: (() => {
          const buf = new ArrayBuffer(
            audioData.allocationSize({ planeIndex: 0 })
          );
          audioData.copyTo(buf, { planeIndex: 0 });
          return buf;
        })(),
      });
      try {
        audioEncoder.encode(copy);
      } finally {
        copy.close();
      }
    },
    async stop() {
      if (stopped) return;
      stopped = true;
      try {
        if (videoEncoder.state === "configured") await videoEncoder.flush();
      } catch {}
      try {
        if (audioEncoder.state === "configured") await audioEncoder.flush();
      } catch {}
      try {
        videoEncoder.close();
      } catch {}
      try {
        audioEncoder.close();
      } catch {}
      await chain.catch(() => {});
      await output.finalize().catch((e) => onError?.(e));
    },
  };
}
